import React, { useEffect, useState } from 'react';
import { getStreak, getLevel, getNextLevel, getXP } from './PerformanceDashboard.jsx';

const RecapStat = ({ label, value, sub, accent }) => (
    <div className={`flex flex-col items-center rounded-2xl border px-4 py-3.5 ${
        accent
            ? 'border-amber-500/25 bg-amber-500/8'
            : 'border-white/10 bg-slate-900/60'
    }`}>
        <span className={`text-2xl font-bold tabular-nums ${accent ? 'text-amber-300' : 'text-white'}`}>
            {value}
        </span>
        <span className="mt-0.5 text-[11px] text-slate-500 font-medium uppercase tracking-wide">{label}</span>
        {sub && <span className="mt-0.5 text-[10px] text-slate-600">{sub}</span>}
    </div>
);

const StudyRecapModal = ({
    folderName,
    reviewed = 0,
    xpEarned = 0,
    masteryPct = 0,
    onStudyAgain,
    onPlayGame,
    onClose,
}) => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setVisible(true), 30);
        return () => clearTimeout(t);
    }, []);

    const streak = getStreak();
    const xp = getXP();
    const level = getLevel(xp);
    const nextLevel = getNextLevel(xp);
    const levelPct = nextLevel
        ? Math.round(((xp - level.minXP) / (nextLevel.minXP - level.minXP)) * 100)
        : 100;

    const emoji =
        reviewed === 0 ? '👀' :
        reviewed < 5   ? '✅' :
        reviewed < 15  ? '🎯' : '🔥';

    return (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-950/75 backdrop-blur-sm px-4 pb-safe">
            <div
                className={`w-full max-w-sm rounded-2xl border border-white/10 bg-[var(--bg-surface)] shadow-2xl p-6 transition-all duration-300 ${
                    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
            >
                {/* Header */}
                <div className="text-center mb-5">
                    <div className="text-4xl mb-2 select-none">{emoji}</div>
                    <h2 className="text-lg font-bold text-white tracking-tight">Session Complete</h2>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">{folderName}</p>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-2">
                    <RecapStat label="Reviewed" value={reviewed} accent />
                    <RecapStat label="XP Earned" value={`+${xpEarned}`} />
                    <RecapStat label="Streak" value={streak.count} sub="days" />
                </div>

                {/* Mastery bar */}
                {masteryPct > 0 && (
                    <div className="mt-4">
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs text-slate-500">Deck mastery</span>
                            <span className="text-xs font-semibold text-emerald-400 tabular-nums">{masteryPct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-white/8 overflow-hidden">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 transition-all duration-700"
                                style={{ width: `${masteryPct}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Level progress */}
                <div className="mt-4 rounded-xl border border-white/8 bg-slate-900/60 px-4 py-3">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-slate-300">
                            Lv {level.level} · {level.label}
                        </span>
                        {nextLevel && (
                            <span className="text-[10px] text-slate-600">
                                {nextLevel.minXP - xp} XP to {nextLevel.label}
                            </span>
                        )}
                    </div>
                    <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                        <div
                            className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-400 transition-all duration-700"
                            style={{ width: `${levelPct}%` }}
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="mt-5 flex flex-col gap-2">
                    {onPlayGame && (
                        <button
                            onClick={onPlayGame}
                            className="w-full rounded-xl py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 transition"
                        >
                            Play a Game
                        </button>
                    )}
                    <div className="flex gap-2">
                        <button
                            onClick={onStudyAgain}
                            className="flex-1 rounded-xl py-2 text-sm font-medium bg-white/6 border border-white/10 text-slate-200 hover:bg-white/10 transition"
                        >
                            Study Again
                        </button>
                        <button
                            onClick={onClose}
                            className="flex-1 rounded-xl py-2 text-sm font-medium bg-white/6 border border-white/10 text-slate-200 hover:bg-white/10 transition"
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudyRecapModal;
